// src/admin/AdminLayout.jsx
import React, { useState, useEffect } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  PieChart,
  Package,
  Users,
  CalendarDays,
  Landmark,
  BarChart3,
  ShieldCheck,
  Search,
  LogOut,
  MessageSquareHeart,
  Phone,
} from "lucide-react";
import SleekSignOutDialog from "../../components/ui/SleekSignOutDialog.jsx";
import { api, signOut } from "../../api/client.js";
import { usePortalSessionGuard } from "../../hooks/usePortalSessionGuard.js";
import {
  listReviewsAdmin,
  isCallbackPending,
  CALLBACKS_CHANGED_EVENT,
} from "../../api/reviews.js";

const navItems = [
  { to: "/admin", label: "Overview", icon: PieChart, end: true },
  { to: "/admin/calendar", label: "Diary", icon: CalendarDays },
  { to: "/admin/products", label: "Product Management", icon: Package },
  { to: "/admin/analytics", label: "Analytics & Reports", icon: BarChart3 },
  { to: "/admin/reviews", label: "Reviews & Engagement", icon: MessageSquareHeart },
  { to: "/admin/callbacks", label: "Requested Callbacks", icon: Phone, badgeKey: "callbacks" },

  { to: "/admin/lenders", label: "Lender Management", icon: Landmark },
  { to: "/admin/borrowers", label: "Borrower Management", icon: Users },

  { to: "/admin/users", label: "User Role & Management", icon: ShieldCheck },
  
];


export default function AdminLayout() {
  const navigate = useNavigate();
  usePortalSessionGuard("admin");
  const [showSignOutModal, setShowSignOutModal] = useState(false);
  const [adminName, setAdminName] = useState("");
  const [adminEmail, setAdminEmail] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [callbackCount, setCallbackCount] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const loadCallbackCount = async () => {
      try {
        const listRes = await listReviewsAdmin({ page: 1, page_size: 200 });
        const all = Array.isArray(listRes.data) ? listRes.data : [];
        if (!cancelled) setCallbackCount(all.filter((item) => isCallbackPending(item)).length);
      } catch {
        if (!cancelled) setCallbackCount(0);
      }
    };
    loadCallbackCount();
    const onChanged = () => loadCallbackCount();
    window.addEventListener(CALLBACKS_CHANGED_EVENT, onChanged);
    const timer = window.setInterval(loadCallbackCount, 60000);
    return () => {
      cancelled = true;
      window.removeEventListener(CALLBACKS_CHANGED_EVENT, onChanged);
      window.clearInterval(timer);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function fetchUser() {
      try {
        const res = await api.get("/api/method/frappe.auth.get_logged_user_info");
        const data = res?.data?.message || res?.data || res;
        if (cancelled || !data) return;
        const fullName = data.full_name || data.fullname || data.name || "";
        const email = data.email || data.name || "";
        setAdminName(fullName || email || "Admin");
        setAdminEmail(email);
      } catch (err) {
        if (cancelled) return;
        console.warn("Failed to fetch admin user info:", err?.message);
        setAdminName("Admin");
      }
    }
    fetchUser();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleSignOutClick = () => {
    setShowSignOutModal(true);
  };

  const handleConfirmSignOut = async () => {
    await signOut();
    navigate("/admin/signin", { replace: true });
  };

  const performSearch = async (query) => {
    const q = query.trim();
    if (!q) {
      setSearchResults([]);
      return;
    }
    setSearching(true);
    try {
      const res = await api.get("/api/method/dynesourcerapi.api.admin_global_search", {
        params: { q },
      });
      const data = res?.data?.message || res?.data || res;
      const rows = Array.isArray(data?.results) ? data.results : Array.isArray(data) ? data : [];
      setSearchResults(rows.slice(0, 10));
    } catch (err) {
      console.warn("Admin global search failed:", err?.message);
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-100 flex flex-col">
      {/* Top bar */}
      <header className="h-20 bg-white shadow-sm border-b border-slate-200 flex items-center justify-between px-6 md:px-10 rounded-b-3xl">
        <div className="flex items-center gap-3">
          <div className="h-12 w-12 rounded-2xl bg-sky-100 flex items-center justify-center">
            <PieChart className="h-6 w-6 text-sky-500" />
          </div>
          <div>
            <div className="text-xl font-semibold text-slate-900">
              Admin Portal
            </div>
            <div className="text-xs text-slate-500">
              Dynesourcer platform control
            </div>
          </div>
        </div>

        <div className="flex items-center gap-4">
          <div className="hidden md:flex items-center px-4 py-2 rounded-full border border-slate-200 bg-slate-50 gap-2 w-80 lg:w-[420px]">
            <Search className="h-4 w-4 text-slate-400" />
            <input
              type="text"
              placeholder="Search across admin…"
              className="bg-transparent text-xs outline-none flex-1"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  performSearch(searchTerm);
                }
              }}
            />
          </div>
          <div className="flex items-center gap-2 pl-4 border-l border-slate-200">
            <div className="h-10 w-10 rounded-full bg-sky-500 text-white flex items-center justify-center text-sm font-semibold">
              {adminName
                .split(" ")
                .map((n) => n[0])
                .join("")
                .slice(0, 2)
                .toUpperCase()}
            </div>
            <div className="hidden md:block">
              <div className="text-sm font-semibold text-slate-900">
                {adminName || "Admin"}
              </div>
              <div className="text-xs text-slate-500">{adminEmail}</div>
            </div>
          </div>

          {/* Top-bar sign out */}
          <button
            type="button"
            onClick={handleSignOutClick}
            className="ml-2 text-xs font-medium text-slate-500 hover:text-red-500 inline-flex items-center gap-1"
          >
            <LogOut className="h-4 w-4" />
            <span>Sign out</span>
          </button>
        </div>
      </header>

      <div className="flex flex-1 overflow-hidden mt-2">
        {/* Sidebar */}
        <aside className="hidden md:flex w-64 flex-col bg-white border-r border-slate-200 rounded-tr-3xl">
          <nav className="mt-4 px-3 pb-6 space-y-1 flex-1">
            {navItems.map((item) => {
              const Icon = item.icon;
              const badge = item.badgeKey === "callbacks" ? callbackCount : 0;
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  className={({ isActive }) =>
                    [
                      "flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium",
                      "transition-colors",
                      isActive
                        ? "bg-sky-50 text-sky-600 border border-sky-100"
                        : "text-slate-600 hover:bg-slate-50",
                    ].join(" ")
                  }
                >
                  <span className="h-8 w-8 rounded-full bg-slate-100 flex items-center justify-center">
                    <Icon className="h-4 w-4 text-slate-700" />
                  </span>
                  <span className="flex-1 min-w-0">{item.label}</span>
                  {badge > 0 ? (
                    <span className="inline-flex min-w-[1.25rem] h-5 items-center justify-center rounded-full bg-sky-600 px-1.5 text-[11px] font-bold text-white">
                      {badge > 99 ? "99+" : badge}
                    </span>
                  ) : null}
                </NavLink>
              );
            })}
          </nav>
        </aside>

        {/* Main content */}
        <main className="flex-1 overflow-y-auto p-4 md:p-6 lg:p-8 relative">
          {searchResults.length > 0 && (
            <div className="absolute right-4 top-0 mt-0 w-full max-w-md bg-white border border-slate-200 rounded-2xl shadow-xl z-20">
              <div className="px-4 py-2 border-b border-slate-100 flex items-center justify-between">
                <span className="text-xs font-semibold text-slate-700">
                  Search results for “{searchTerm}”
                </span>
                <button
                  type="button"
                  className="text-[11px] text-slate-400 hover:text-slate-600"
                  onClick={() => setSearchResults([])}
                >
                  Close
                </button>
              </div>
              <div className="max-h-80 overflow-y-auto">
                {searchResults.map((row, idx) => (
                  <button
                    key={row.route || row.path || row.name || idx}
                    type="button"
                    className="w-full px-4 py-2 text-left text-xs hover:bg-slate-50 border-b border-slate-50 last:border-b-0"
                    onClick={() => {
                      const route =
                        row.route ||
                        row.path ||
                        row.url ||
                        row.link ||
                        "/admin";
                      setSearchResults([]);
                      if (route.startsWith("http")) {
                        window.open(route, "_blank", "noreferrer");
                      } else {
                        navigate(route);
                      }
                    }}
                  >
                    <div className="font-semibold text-slate-800 truncate">
                      {row.title || row.label || row.name || row.route || row.path || row.url || row.link || ""}
                    </div>
                    {row.subtitle || row.doctype || row.type ? (
                      <div className="text-[11px] text-slate-500 truncate">
                        {row.subtitle || row.doctype || row.type}
                      </div>
                    ) : null}
                  </button>
                ))}
                {searchResults.length === 0 && !searching && (
                  <div className="px-4 py-3 text-xs text-slate-500">
                    No matches found.
                  </div>
                )}
              </div>
            </div>
          )}
          <Outlet />
        </main>
      </div>

      {/* Sign Out Confirmation Modal */}
      <SleekSignOutDialog
        isOpen={showSignOutModal}
        onClose={() => setShowSignOutModal(false)}
        onConfirm={handleConfirmSignOut}
      />
    </div>
  );
}